<template>
<div class="portal-progress-div">
    <div class="progress-div" v-for="(item, index) in progressItems" :key="`progress-${index}`">
        <div class="portal_progress-box">
            <!-- Program Info Section -->
            <div class="program-div">
                <div class="program-info-div">
                    <div class="program-image-div">
                        <img sizes="(max-width: 767px) 100vw, (max-width: 991px) 728px, 931px" :srcset="getProductSrcSet(item.product_id)" :alt="getFullProductName(item.product_id)" :src="getProductImage(item.product_id)" loading="lazy" class="program-image">
                    </div>
                    <div>
                        <div class="program-name-text">{{ getFullProductName(item.product_id) }}</div>
                        <div class="next-payment-text"></div>

                    </div>
                </div>
                <div class="progress_statusdiv">
                    <img loading="lazy" :src="getStatusIcon(item.status)" alt="" class="image-476" :class="{'review': isNeedsReview(item.status)}">
                    <div class="text-block-1421">{{ getStatusText(item.status) }}</div>
                </div>
            </div>

            <!-- Progress Steps Section -->
            <div class="progress-drop-div">
                <div class="progress-bullet" v-for="(step, stepIndex) in getVisibleSteps(item)" :key="`step-${stepIndex}`">
                    <img loading="lazy" :src="getStepIcon(step.status, item.status)" alt="" class="image-442" :class="{'review': isNeedsReview(item.status)}">
                    <div class="progress-bullet-text-div">
                        <div class="text-block-930" :class="{'strong': isCurrentStep(step.status, item.status)}">
                            <strong v-if="isCurrentStep(step.status, item.status)">{{ step.title }}</strong>
                            <template v-else>{{ step.title }}</template>
                        </div>
                        <div class="text-block-931" v-html="step.description"></div>
                        <template v-if="step.action && (step.status === item.status || 
                        (step.status === 'pending_approval' && item.status === 'pending_approval') ||
                        (step.status === 'prescription_received' && item.status === 'prescription_received'))">
                            <component v-if="!step.isFunction" :is="step.linkType" :to="step.link" :href="step.link" class="link-58" target="_blank">
                                {{ step.action }} <span class="text-span-234">-&gt;</span>
                            </component>
                            <a v-else href="#" class="link-58" @click.prevent="executeFunction(step.functionName, item)">
                                {{ step.action }} <span class="text-span-234">-&gt;</span>
                            </a>
                        </template>
                        <!-- Show Adjust Doses link for prescription_received status with submitted == 4 -->
                        <template v-if="step.status === 'prescription_received' && item.status === 'prescription_received' && item.submitted === 4">
                            <router-link :to="`/adjust-doses/${item.id}`" class="link-58">
                                Adjust Doses <span class="text-span-234">-&gt;</span>
                            </router-link>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Progress CTA Section (Marketing) -->
    <div class="progress-cta-div">
        <div class="portal-cta-div" v-for="(_, ctaIndex) in 3" :key="`cta-${ctaIndex}`">
            <div class="portal-cta-text-div">
                <div class="text-block-916"><span class="text-span-200">Shop Responsibly</span><em>.</em> Save Seriously</div>
                <div class="text-block-917">Foundational health support is never a maybe. Get 40% off any new item you add to your subscription.</div>
            </div>
            <a href="#" class="button-94 w-button">Shop the Sale</a>
        </div>
    </div>
</div>
</template>

<script>
import html2pdf from 'html2pdf.js';
import {
    getProductById,
} from '@/utils/productData'

export default {
    name: 'ProgressTracker',

    props: {
        progressItems: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            baseUrl: `${process.env.VUE_APP_API_ENDPOINT}`,
            iconPaths: {
                check: require('@/assets/images/icon_check-black2.svg'),
                urgent: require('@/assets/images/icon_Urgent.svg'),
                open: require('@/assets/images/icon_open.svg') // Make sure this path is correct
            },
            // Define the status flow order
            statusFlow: [
                'payment',
                'pending_approval',
                'waiting_prescription',
                'prescription_received',
                'order_placed',
                'order_shipped',
                'order_delivered'
            ]
        }
    },

    methods: {
        getFullProductName(id) {
            try {
                let product = getProductById(id);
                return product[0].name;
            } catch (error) {
                return 'Tirzepatide';
            }
        },

        getNextPaymentDate(item) {
            return item.next_billing_date ? this.formatDate(item.next_billing_date) : '12/30/24';
        },

        formatDate(dateString) {
            if (!dateString) return '';

            try {
                const date = new Date(dateString);
                return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear().toString().slice(2)}`;
            } catch (error) {
                return dateString;
            }
        },

        getStatusIcon(status) {
            if (status === 'order_delivered' || status === 'complete') {
                return this.iconPaths.check;
            } else if (this.isNeedsReview(status)) {
                return this.iconPaths.urgent;
            } else {
                return this.iconPaths.urgent; // Default icon
            }
        },

        isNeedsReview(status) {
            return ['pending_approval', 'needs_review'].includes(status);
        },

        getStatusText(status) {
            if (status === 'order_delivered' || status === 'complete') {
                return 'Complete';
            } else if (this.isNeedsReview(status)) {
                return 'Needs Review';
            } else {
                return 'Processing';
            }
        },

        getSteps(item) {
            return [{
                    status: 'payment',
                    title: 'Successful Payment',
                    description: 'Complete payment for your medication.',
                    action: 'Make Payment',
                    link: '/payment',
                    linkType: 'router-link',
                    isFunction: false
                },
                {
                    status: 'pending_approval',
                    title: 'Medical Questionnaire',
                    description: 'This will be reviewed by our doctor in order to prescribe your medication.',
                    action: 'Complete Medical Form',
                    link: `/medical-form/${item.id || ''}`,
                    linkType: 'router-link',
                    isFunction: false
                },
                {
                    status: 'waiting_prescription',
                    title: 'Final Doctor Approval',
                    description: 'Your doctor is reviewing your records. Please allow 24-48 hours.',
                    action: '',
                    link: '#',
                    linkType: 'a',
                    isFunction: false,
                    functionName: 'showImageUpload'
                },
                {
                    status: 'prescription_received',
                    title: 'Prescription Approved',
                    description: 'Your prescription has been approved by the doctor.',
                    action: '',
                    link: '#',
                    linkType: 'a',
                    isFunction: false,
                    functionName: ''
                },
                {
                    status: 'order_placed',
                    title: 'Pharmacy is Fulfilling the order',
                    description: 'This usually takes 2-3 days to complete. Except on Sundays and recognized holidays.',
                    action: '',
                    link: '#',
                    linkType: 'a',
                    isFunction: false
                },
                {
                    status: 'shipping_paused',
                    title: 'Shipping paused.',
                    description: 'You requested us to hold Shipping.',
                    action: '',
                    link: '#',
                    linkType: 'a',
                    isFunction: false
                },
                {
                    status: 'order_shipped',
                    title: 'Medication Has Shipped',
                    description: this.getTrackingInfo(item),
                    action: 'Track Shipment',
                    link: this.getTrackingLink(item),
                    linkType: 'a',
                    isFunction: false
                },
                {
                    status: 'order_delivered',
                    title: 'Medication Delivered',
                    description: 'Your medication was successfully delivered to your home.',
                    action: 'View Delivery Details',
                    link: '#',
                    linkType: 'a',
                    isFunction: false
                }
            ];
        },

        getTrackingInfo(item) {
            const carrier = item.logistics_provider || 'UPS';
            const trackingNumber = item.tracking_number || '';

            if (trackingNumber) {
                return `Carrier: <strong>${carrier}</strong><br>Tracking Number: <strong>${trackingNumber}</strong>`;
            } else {
                return 'Packages are sent 2 Day Express. Except on Sundays and recognized holidays.';
            }
        },

        getTrackingLink(item) {
            const provider = item.logistics_provider || 'UPS';
            const trackingNumber = item.tracking_number || '';

            if (!trackingNumber) return '#';

            if (provider.toLowerCase() === 'fedex') {
                return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
            } else {
                return `https://www.ups.com/track?tracknum=${trackingNumber}`;
            }
        },
        getProductSrcSet(id) {
            let product = this.getFullProductName(id);
            let img = `https://ohmymeds.s3.us-east-2.amazonaws.com/public/assets/product_image/webp/${product.toLowerCase()}/1.webp`;
            return `${img} 500w, ${img} 800w, ${img} 1080w`
        },

        getProductImage(id) {
            let product = this.getFullProductName(id);
            console.log(product)
            return `https://ohmymeds.s3.us-east-2.amazonaws.com/public/assets/product_image/webp/${product.toLowerCase()}/1.webp`;
        },
        // Show all steps
        getVisibleSteps(item) {
            // Get all steps first
            const allSteps = this.getSteps(item);

            // Filter steps based on current status
            const filteredSteps = allSteps.filter(step => {
                if (step.status === 'shipping_paused') {
                    return item.status === 'shipping_paused';
                }

                if (step.status === 'order_placed') {
                    // This is because when an order is paused, it means it was paused before being placed
                    return item.status !== 'shipping_paused';
                }

                // Include all other steps normally
                return true;
            });

            return filteredSteps;
        },

        // Correctly determine which icon to show for each step
        getStepIcon(stepStatus, currentStatus) {
            // First check if this is the CURRENT step (exact match)
            if (stepStatus === currentStatus) {
                return this.iconPaths.urgent; // Urgent icon for current step
            }

            // When order is paused, we need to determine completion based on logical flow
            let effectiveCurrentStatus = currentStatus;
            if (currentStatus === 'shipping_paused') {
                // If order is paused, we assume it was paused after order_placed
                // This allows previous steps to show as completed
                effectiveCurrentStatus = 'order_placed';
            }

            // Get indices in the status flow using effective status
            const currentFlowIndex = this.statusFlow.indexOf(effectiveCurrentStatus);
            const stepFlowIndex = this.statusFlow.indexOf(stepStatus);

            // Check if it's a COMPLETED step
            // A step is completed if it comes before the current step in the flow
            if (stepFlowIndex !== -1 && currentFlowIndex !== -1 && stepFlowIndex < currentFlowIndex) {
                return this.iconPaths.check; // Check icon for completed steps
            }

            // Handle special case where current status is not in statusFlow
            // This can happen with custom statuses like 'complete', 'needs_review', etc.
            if (currentFlowIndex === -1) {
                // For statuses not in flow, we'll use a fallback logic
                // Assume common completion statuses mean most steps are done
                if (['complete', 'order_delivered'].includes(currentStatus)) {
                    // Most steps should be completed for these statuses
                    if (!['order_shipped', 'order_delivered'].includes(stepStatus)) {
                        return this.iconPaths.check;
                    }
                }

                // For other unknown statuses, assume early steps are completed
                if (['payment', 'pending_approval', 'waiting_prescription'].includes(stepStatus)) {
                    return this.iconPaths.check;
                }
            }

            // Finally, must be a FUTURE step
            return this.iconPaths.open; // Open icon for future steps
        },

        isCurrentStep(stepStatus, currentStatus) {
            return stepStatus === currentStatus;
        },

        executeFunction(functionName, item) {
            if (functionName === 'showPrescription') {
                this.$emit('showModel', item.prescription_data);
            } else if (functionName === 'showImageUpload') {
                this.$emit('showImageUpload', item.id);
            } else if (functionName === 'downloadInvoice') {
                this.downloadInvoice(item.payment_intent_id, this.getFullProductName(item.product_id));
            }
        },

        async downloadInvoice(paymentIntentId, program) {
            try {
                const response = await this.$axios.get(`${this.baseUrl}/api/invoice?id=${paymentIntentId}&program=${program}`);

                if (response.data.status == 'error') {
                    console.log("Error generating invoice");
                } else {
                    const options = {
                        margin: 0,
                        filename: `${paymentIntentId}.pdf`,
                        image: {
                            type: 'jpeg',
                            quality: 1
                        },
                        html2canvas: {
                            dpi: 300,
                            scale: 4,
                            letterRendering: true,
                            useCORS: true,
                            windowWidth: 794,
                            windowHeight: 1123,
                            scrollX: 0,
                            scrollY: 0,
                            onrendered: function (canvas) {
                                canvas.style.imageRendering = 'pixelated';
                                canvas.style.imageRendering = '-webkit-optimize-contrast';
                            }
                        },
                        jsPDF: {
                            unit: 'mm',
                            format: [210, 297],
                            orientation: 'portrait',
                            compress: true,
                            precision: 16,
                            userUnit: 1.0,
                            textColor: "#000000",
                            fillColor: "#000000"
                        }
                    };

                    html2pdf().set(options).from(response.data).save();
                }
            } catch (error) {
                console.error('Error downloading invoice:', error);
            }
        }
    }
};
</script>

<style>
.image-442 {
    z-index: 998;
    background-color: transparent;
    border-radius: 3px;
    width: 15px;
    margin-right: 10px;
    position: relative;
}
</style>
